/**
 * Main-product integration for Teeinblue and Impulse.
 * Controls the mobile sticky gallery independently of the form integration.
 * App-owned prices, field values, translations and cart behavior stay native.
 */
(() => {
  'use strict';

  // Prevent duplicate initialization when this asset is loaded more than once.
  if (window.TeeStickyGallery) return;

  const SELECTORS = {
    root: '[data-teeinblue-pdp]',
    form: '#tee-artwork-form',
    inputs: '#teeFormInputs',
    personalizationHeader: '.tee-customize-main-form > .tee-form-header',
    gallery: '#tee-gallery',
    galleryWrapper: '.tee-gallery-wrapper',
    slider: '.tee-slider',
    dialogs: '.tee-dialog-image-cropper, .tee-dialog, #teeinblue-wrapper [role="dialog"], .pdp-size-guide__dialog[open]',
    dialogChanges: '.tee-dialog-image-cropper, .tee-dialog, #teeinblue-wrapper, .pdp-size-guide__dialog'
  };

  const CLASSES = {
    sticky: 'tee-gallery-sticky',
    hidden: 'tee-gallery-hidden'
  };

  const STICKY = {
    mobileBreakpoint: 768,
    // Keep the full/compact thresholds wider than the release-check jitter.
    activationOffset: -64,
    releaseOffset: 0,
    boundaryBuffer: 96
  };

  const instances = new Map();
  const lifecycle = new AbortController();

  function isDialogVisible(dialog) {
    const style = getComputedStyle(dialog);
    const bounds = dialog.getBoundingClientRect();

    return dialog.getAttribute('aria-hidden') !== 'true' &&
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      Number(style.opacity) !== 0 &&
      bounds.width > 0 && bounds.height > 0;
  }

  class StickyGallery {
    constructor(root) {
      this.root = root;
      this.frame = 0;
      this.galleryHidden = false;
      this.stickyActive = false;
      this.overlayVisible = false;
      this.presentationAnimation = null;
      this.presentationVersion = 0;
      this.gallery = null;
      this.galleryWrapper = null;
      this.closeButton = null;
      this.openButton = null;
      this.galleryEvents = null;
      this.events = new AbortController();

      // Coalesce scroll, resize and DOM changes into one update per frame.
      this.scheduleUpdate = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          this.update();
        });
      };

      // Geometry changes may happen without scrolling (uploads, font loading,
      // native form reinjection or expanding a conditional field).
      this.sizeObserver = new ResizeObserver(this.scheduleUpdate);
      this.sizeObserver.observe(root);
      this.observedInputs = null;
      this.galleryStyleObserver = new MutationObserver(this.scheduleUpdate);
      this.observeChanges();
      this.bindViewportEvents();
      this.update();
    }

    observeChanges() {
      this.formObserver = new (window.FizzyProductObserver?.Observer || MutationObserver)(this.scheduleUpdate);
      this.formObserver.observe(this.root, { childList: true, subtree: true });

      // Teeinblue can render editor dialogs outside the product section.
      this.dialogObserver = new MutationObserver(records => {
        const relevant = records.some(record =>
          record.target.closest?.(SELECTORS.dialogChanges) ||
          (record.type === 'childList' && [...record.addedNodes, ...record.removedNodes].some(node =>
            node.nodeType === 1 && (node.matches(SELECTORS.dialogChanges) || node.querySelector(SELECTORS.dialogChanges))
          ))
        );
        if (relevant) this.scheduleUpdate();
      });
    }

    syncDialogObserver(active) {
      if (active === this.dialogObserved) return;
      this.dialogObserved = active;
      if (active) this.dialogObserver.observe(document.body, {
        childList: true, subtree: true, attributes: true,
        attributeFilter: ['class', 'style', 'aria-hidden', 'open']
      });
      else this.dialogObserver.disconnect();
    }

    bindViewportEvents() {
      const options = { passive: true, signal: this.events.signal };
      window.addEventListener('scroll', this.scheduleUpdate, options);
      window.addEventListener('resize', this.scheduleUpdate, options);
    }

    update() {
      if (!this.root.isConnected) return;

      const form = this.root.querySelector(SELECTORS.form);
      this.updateGallery(form);
    }

    updateGallery(form) {
      const gallery = this.root.querySelector(SELECTORS.gallery);
      if (gallery !== this.gallery) this.attachGallery(gallery);

      const inputs = form?.querySelector(SELECTORS.inputs);
      if (!gallery || !this.galleryWrapper || !inputs) {
        this.clearPersonalizationSpace();
        this.syncDialogObserver(false);
        this.setStickyState(false, true);
        return;
      }

      if (inputs !== this.observedInputs) {
        this.clearPersonalizationSpace();
        if (this.observedInputs) this.sizeObserver.unobserve(this.observedInputs);
        this.observedInputs = inputs;
        this.sizeObserver.observe(inputs);
      }

      const wasSticky = this.stickyActive;
      const eligible = window.innerWidth < STICKY.mobileBreakpoint &&
        window.innerHeight > window.innerWidth &&
        this.root.dataset.teeGalleryEditor !== 'true' && !window.Shopify?.designMode;
      this.syncDialogObserver(eligible);
      if (!eligible) {
        this.clearPersonalizationSpace();
        this.setStickyState(false, true);
        return;
      }
      // Keep the header cart reachable before opening a drawer; use the existing
      // coalesced geometry pass instead of another scroll listener/observer.
      const header = document.getElementById('SiteHeader');
      const headerBottom = Math.max(0, header?.getBoundingClientRect().bottom || 0);
      const stickyTop = `${headerBottom}px`;
      if (this.root.style.getPropertyValue('--fizzy-sticky-top') !== stickyTop) {
        this.root.style.setProperty('--fizzy-sticky-top', stickyTop);
      }
      const editorOpen = [...document.querySelectorAll(SELECTORS.dialogs)].some(isDialogVisible);
      if (editorOpen) {
        this.setStickyState(false, true);
        return;
      }

      // Measure only in normal flow. Temporarily unfixing the gallery during
      // scroll can trigger browser scroll anchoring and move the whole page.
      const wasFixed = this.root.classList.contains(CLASSES.sticky);
      if (wasFixed && this.measurementWidth !== window.innerWidth) {
        // A real width change needs one fresh natural measurement. Mobile
        // browser chrome changes only the height and must not reset the layout.
        this.setStickyState(false, true);
        this.scheduleUpdate();
        return;
      }
      if (!wasFixed) {
        const naturalHeight = this.galleryWrapper.getBoundingClientRect().height;
        this.naturalPreviewHeight = gallery.querySelector(SELECTORS.slider)?.getBoundingClientRect().height || 0;
        this.measurementWidth = window.innerWidth;
        const placeholder = `${naturalHeight}px`;
        if (this.galleryWrapper.style.getPropertyValue('--tee-sticky-placeholder-height') !== placeholder) {
          this.galleryWrapper.style.setProperty('--tee-sticky-placeholder-height', placeholder);
        }
      }
      const rawScale = Number(getComputedStyle(this.root).getPropertyValue('--tee-sticky-scale'));
      const scale = Number.isFinite(rawScale) && rawScale > 0 ? rawScale : .7;
      const previewHeight = (this.naturalPreviewHeight || 0) * scale;
      const stickyHeight = `${previewHeight}px`;
      if (this.root.style.getPropertyValue('--tee-sticky-preview-height') !== stickyHeight) {
        this.root.style.setProperty('--tee-sticky-preview-height', stickyHeight);
      }

      // Begin at the personalization title, directly after product/size controls;
      // the end still belongs only to the input fields, never purchase controls.
      const inputBounds = inputs.getBoundingClientRect();
      if (!(inputBounds.width > 0 && inputBounds.height > 0)) {
        this.clearPersonalizationSpace();
        this.setStickyState(false, true);
        return;
      }
      const titleBounds = form.querySelector(SELECTORS.personalizationHeader)?.getBoundingClientRect();
      const reserved = this.root.hasAttribute('data-tee-sticky-personalization-space')
        ? Number.parseFloat(this.root.style.getPropertyValue('--tee-sticky-input-gap')) || 0 : 0;
      // The title stays outside our reserved space. Its position cannot feed
      // back into the activation boundary when the preview makes room below it.
      const start = (titleBounds?.height > 0 ? titleBounds.top : inputBounds.top - reserved) - headerBottom;
      const boundary = inputBounds.bottom - headerBottom;
      const startLimit = wasSticky ? STICKY.releaseOffset : STICKY.activationOffset;
      // Once dismissed at the form end, return clearly into the form before
      // showing the overlay again. Small scroll reversals must not flash it.
      const endLimit = previewHeight + (wasSticky ? 0 : Math.max(STICKY.boundaryBuffer, previewHeight));
      const active = previewHeight > 0 && start < startLimit && boundary > endLimit;
      // Keep the space at the lower exit/dismissal; collapsing it there would
      // move the remaining controls and feed back into the exit boundary.
      if (start >= STICKY.releaseOffset) this.clearPersonalizationSpace();
      if (active && !this.root.hasAttribute('data-tee-sticky-personalization-space')) {
        const style = getComputedStyle(inputs);
        const contentTop = inputBounds.top + (Number.parseFloat(style.paddingTop) || 0);
        const gap = Math.max(0, headerBottom + previewHeight + 12 - contentTop);
        this.root.style.setProperty('--tee-sticky-input-margin', `${Number.parseFloat(style.marginTop) || 0}px`);
        this.root.style.setProperty('--tee-sticky-input-gap', `${gap}px`);
        this.root.setAttribute('data-tee-sticky-personalization-space', '');
      }
      // Remove the overlay immediately on return above personalization so it
      // cannot fade over size controls; retain the existing bottom exit fade.
      this.setStickyState(active, wasFixed && start >= STICKY.releaseOffset);
    }

    clearPersonalizationSpace() {
      if (!this.root.hasAttribute('data-tee-sticky-personalization-space')) return;
      this.root.removeAttribute('data-tee-sticky-personalization-space');
      this.root.style.removeProperty('--tee-sticky-input-gap');
      this.root.style.removeProperty('--tee-sticky-input-margin');
    }

    attachGallery(gallery) {
      // Reinjection replaces controls instead of accumulating buttons or listeners.
      this.releaseGallery();
      if (!gallery) return;

      // A vendor reinjection may clone our presentation controls. Remove only
      // this component's stale controls before attaching the new instance.
      gallery.querySelectorAll('.tee-sticky-close').forEach(button => button.remove());
      this.root.querySelectorAll('.tee-sticky-open').forEach(button => button.remove());
      this.gallery = gallery;
      this.galleryStyleObserver.observe(gallery, { attributes: true, attributeFilter: ['style'] });
      this.sizeObserver.observe(gallery);
      this.galleryWrapper = gallery.closest(SELECTORS.galleryWrapper);
      if (this.galleryWrapper) this.sizeObserver.observe(this.galleryWrapper);
      this.galleryEvents = new AbortController();
      this.closeButton = this.createGalleryButton('close', this.root.dataset.teeGalleryHide);
      this.openButton = this.createGalleryButton('open', this.root.dataset.teeGalleryShow);
      gallery.append(this.closeButton);
      this.root.append(this.openButton);
    }

    createGalleryButton(action, label) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `tee-sticky-${action}`;
      button.setAttribute('aria-label', label);
      button.setAttribute('aria-controls', 'tee-gallery');
      button.addEventListener('click', event => {
        event.preventDefault();
        event.stopPropagation();
        this.galleryHidden = action === 'close';
        this.updateGallery(this.root.querySelector(SELECTORS.form));
        const nextButton = action === 'close' ? this.openButton : this.closeButton;
        nextButton.focus({ preventScroll: true });
      }, { signal: this.galleryEvents.signal });
      return button;
    }

    setStickyState(active, immediate = false) {
      this.stickyActive = active;
      const closeHidden = !active || this.galleryHidden;
      if (this.closeButton && this.closeButton.hidden !== closeHidden) this.closeButton.hidden = closeHidden;
      const openHidden = !active || !this.galleryHidden;
      if (this.openButton && this.openButton.hidden !== openHidden) this.openButton.hidden = openHidden;
      const expanded = String(active && !this.galleryHidden);
      if (this.openButton && this.openButton.getAttribute('aria-expanded') !== expanded) this.openButton.setAttribute('aria-expanded', expanded);

      const visible = active && !this.galleryHidden;
      if (visible === this.overlayVisible && !immediate) return;
      this.overlayVisible = visible;
      const version = ++this.presentationVersion;
      const wasFixed = this.root.classList.contains(CLASSES.sticky);
      const wasHidden = this.root.classList.contains(CLASSES.hidden);
      // Sample a running transition before cancelling it, so quick reversals
      // continue from the displayed frame rather than jumping to an endpoint.
      const style = this.gallery && getComputedStyle(this.gallery);
      const from = wasFixed && !wasHidden && style
        ? { opacity: style.opacity }
        : { opacity: 0 };
      this.presentationAnimation?.cancel();
      this.presentationAnimation = null;

      const finish = () => {
        if (version !== this.presentationVersion) return;
        this.root.classList.toggle(CLASSES.sticky, this.stickyActive);
        this.root.classList.toggle(CLASSES.hidden, this.galleryHidden);
        this.presentationAnimation = null;
      };
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!this.gallery || immediate || reducedMotion || !this.gallery.animate || (!visible && (!wasFixed || wasHidden))) {
        finish();
        return;
      }
      // Keep fixed positioning and the layout reservation until exit completes.
      // Native gallery nodes, uploads and horizontal gestures are not replaced.
      this.root.classList.add(CLASSES.sticky);
      this.root.classList.remove(CLASSES.hidden);
      const animation = this.gallery.animate([
        from,
        { opacity: visible ? 1 : 0 }
      ], { duration: visible ? 180 : 140, easing: 'cubic-bezier(.2, .7, .2, 1)' });
      this.presentationAnimation = animation;
      animation.onfinish = finish;
    }

    releaseGallery() {
      this.galleryEvents?.abort();
      this.galleryStyleObserver.disconnect();
      if (this.gallery) this.sizeObserver.unobserve(this.gallery);
      if (this.galleryWrapper) {
        this.sizeObserver.unobserve(this.galleryWrapper);
        this.galleryWrapper.style.removeProperty('--tee-sticky-placeholder-height');
      }
      this.setStickyState(false, true);
      this.closeButton?.remove();
      this.openButton?.remove();
      this.galleryEvents = null;
      this.closeButton = null;
      this.openButton = null;
      this.gallery = null;
      this.galleryWrapper = null;
      this.naturalPreviewHeight = 0;
      this.measurementWidth = null;
    }

    destroy() {
      this.clearPersonalizationSpace();
      cancelAnimationFrame(this.frame);
      this.sizeObserver.disconnect();
      this.formObserver.disconnect();
      this.dialogObserver.disconnect();
      this.events.abort();
      this.releaseGallery();
      this.root.classList.remove(CLASSES.hidden);
      this.root.style.removeProperty('--tee-sticky-preview-height');
      this.root.style.removeProperty('--fizzy-sticky-top');
    }
  }

  function mount(scope = document) {
    const roots = [...scope.querySelectorAll(SELECTORS.root)];
    if (scope.matches?.(SELECTORS.root)) roots.push(scope);

    roots.forEach(root => {
      if (!instances.has(root)) instances.set(root, new StickyGallery(root));
    });
  }

  function unmount(scope) {
    instances.forEach((instance, root) => {
      if (root === scope || scope.contains(root)) {
        instance.destroy();
        instances.delete(root);
      }
    });
  }

  // Shopify section lifecycle and Teeinblue reinjection share the same registry.
  const options = { signal: lifecycle.signal };
  document.addEventListener('shopify:section:load', event => mount(event.target), options);
  document.addEventListener('shopify:section:unload', event => unmount(event.target), options);
  document.addEventListener('teeinblue-event-component-injected', () => mount(), options);

  mount();
  window.TeeStickyGallery = {
    destroy() {
      lifecycle.abort();
      instances.forEach(instance => instance.destroy());
      instances.clear();
      delete window.TeeStickyGallery;
    }
  };
})();

/* Temporary opt-in QA recorder. Inactive on ordinary product URLs. */
(() => {
  'use strict';
  if (new URLSearchParams(location.search).get('fizzy_scroll_trace') !== '1') return;
  if (document.getElementById('fizzy-scroll-trace')) return;
  const started = performance.now();
  const viewport=window.visualViewport;
  const events = [];
  const controller = new AbortController();
  const options = {passive:true,signal:controller.signal};
  let stopped=false, frame=0, touch=false, lastSample=null;
  const panel=document.createElement('div');
  panel.id='fizzy-scroll-trace';
  panel.style.cssText='position:fixed;right:8px;bottom:8px;z-index:2147483647;background:#243443;color:white;padding:8px 10px;border-radius:6px;font:inherit;font-size:12px;max-width:210px;box-shadow:0 1px 5px #0003';
  const status=document.createElement('span');status.textContent='Scroll-Messung läuft';
  const button=document.createElement('button');button.type='button';button.textContent='Log speichern';
  button.style.cssText='display:block;margin:5px 0 0;border:1px solid white;border-radius:4px;background:transparent;color:inherit;font:inherit;padding:4px 8px;cursor:pointer';
  panel.append(status,button);document.body.append(panel);
  const time=()=>Math.round(performance.now()-started);
  function record(type,data={}) {if(!stopped&&events.length<6000)events.push({t:time(),type,...data});}
  function box(el) {if(!el)return null;const r=el.getBoundingClientRect();return {top:Math.round(r.top*10)/10,height:Math.round(r.height*10)/10};}
  function sample(reason) {
    if(stopped)return;
    const root=document.querySelector('[data-teeinblue-pdp]');
    const form=root?.querySelector('#tee-artwork-form');
    const formBox=box(form);
    const state={y:Math.round(scrollY*10)/10,h:innerHeight,w:innerWidth,visualH:Math.round((viewport?.height||innerHeight)*10)/10,touch,
      sticky:!!root?.classList.contains('tee-gallery-sticky'),galleryHidden:!!root?.classList.contains('tee-gallery-hidden'),
      gallery:box(root?.querySelector('#tee-gallery')),galleryWrapper:box(root?.querySelector('.tee-gallery-wrapper')),
      formDocumentTop:formBox?Math.round((formBox.top+scrollY)*10)/10:null,
      header:box(document.getElementById('SiteHeader')),headerPlaceholder:box(document.getElementById('StickyHeaderWrap')),
      htmlOverflow:getComputedStyle(document.documentElement).overflow,bodyOverflow:getComputedStyle(document.body).overflow,
      accordions:[...document.querySelectorAll('[data-teeinblue-pdp] .collapsible-content')].map(el=>({id:el.id,open:el.classList.contains('is-open'),height:Math.round(el.getBoundingClientRect().height*10)/10,inlineHeight:el.style.height})),
      loox:[...document.querySelectorAll('iframe[id^="LOOX"],iframe#looxReviewsFrame')].map(el=>({id:el.id,...box(el)})),
      looxOverlay:!!document.querySelector('#looxOverlay_quickview,.loox-overlay')};
    const signature=JSON.stringify(state);
    if(signature!==lastSample){record('geometry',{reason,...state});lastSample=signature;}
  }
  function schedule(reason) {if(!frame&&!stopped)frame=requestAnimationFrame(()=>{frame=0;sample(reason);});}
  window.addEventListener('scroll',()=>schedule('scroll'),options);
  window.addEventListener('resize',()=>{record('resize');schedule('resize');},options);
  viewport?.addEventListener('resize',()=>{record('visual-resize');schedule('visual-resize');},options);
  document.addEventListener('touchstart',()=>{touch=true;record('touchstart');},options);
  document.addEventListener('touchend',()=>{touch=false;record('touchend');},options);
  document.addEventListener('touchcancel',()=>{touch=false;record('touchcancel');},options);
  document.addEventListener('click',e=>{
    if(panel.contains(e.target))return;
    const el=e.target.closest?.('button,summary,.tee-thumbnail,.tee-sticky-open,.tee-sticky-close');
    if(el)record('click',{tag:el.tagName,id:el.id,class:el.getAttribute('class')?.slice(0,150)});
  },options);
  document.addEventListener('focusin',e=>{if(!panel.contains(e.target))record('focus',{tag:e.target.tagName,id:e.target.id});},options);
  const mutations=new MutationObserver(records=>{
    let relevant=false;
    for(const r of records){const el=r.target;if(el.nodeType!==1||panel.contains(el))continue;
      const target=el.matches('#SiteHeader,#StickyHeaderWrap,[data-teeinblue-pdp],#tee-gallery,.collapsible-content,.tee-sticky-close,.tee-sticky-open,html,body,iframe[id^="LOOX"],iframe#looxReviewsFrame,#looxOverlay_quickview,.loox-overlay');
      if(target&&r.type==='attributes'){record('mutation',{id:el.id,tag:el.tagName,attribute:r.attributeName,old:r.oldValue?.slice(0,220),value:el.getAttribute(r.attributeName)?.slice(0,220)});relevant=true;}
      if(r.type==='childList'&&(el===document.body||el.matches('.tee-gallery-wrapper'))){record('structure',{id:el.id,added:r.addedNodes.length,removed:r.removedNodes.length});relevant=true;}
    }
    if(relevant)schedule('mutation');
  });
  mutations.observe(document.documentElement,{subtree:true,attributes:true,attributeOldValue:true,attributeFilter:['style','class','hidden','open'],childList:true});
  let tasks;
  if(typeof PerformanceObserver==='function')try{tasks=new PerformanceObserver(list=>{for(const entry of list.getEntries())record('longtask',{duration:Math.round(entry.duration),start:Math.round(entry.startTime-started)});});tasks.observe({type:'longtask',buffered:false});}catch{}
  sample('start');
  button.addEventListener('click',()=>{
    sample('export');stopped=true;controller.abort();mutations.disconnect();tasks?.disconnect();if(frame)cancelAnimationFrame(frame);
    const data={schema:1,revision:'observer-guard-2026-10-16',capturedAt:new Date().toISOString(),userAgent:navigator.userAgent,
      assets:[...document.querySelectorAll('script[src]')].map(el=>el.getAttribute('src')).filter(s=>/\/assets\/(theme|tee-sticky-gallery|tee-photo-strip|teeinblue-custom)\.js/.test(s)),events};
    const json=JSON.stringify(data);
    const output=document.createElement('textarea');output.readOnly=true;output.setAttribute('aria-label','Scroll-Messdaten');output.value=json;output.style.cssText='display:block;width:200px;height:70px;margin-top:6px;font-size:11px;color:#111;background:white';panel.append(output);
    const url=URL.createObjectURL(new Blob([json],{type:'application/json'}));
    const a=document.createElement('a');a.href=url;a.download='fizzy-scroll-diagnose.json';a.click();setTimeout(()=>URL.revokeObjectURL(url),60000);
    status.textContent='Messung beendet. JSON-Datei speichern oder Messdaten kopieren.';button.disabled=true;
  });
})();
